/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type {
  ChangeEvent,
  ClipboardEventHandler,
  FocusEventHandler,
  ForwardRefExoticComponent,
  InputEventHandler,
  JSX,
  ReactNode,
  RefAttributes,
} from "react";
import { forwardRef, useEffect, useRef, useState } from "react";
import type { Control, FieldValues, Path } from "react-hook-form";
import { useController } from "react-hook-form";

import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { TextProps } from "../../../_base/_core/Text/Text.emotion";
import { Text } from "../../../_base/_core/Text/Text.emotion";

const divStyle: Interpolation = {
  WebkitUserSelect: "text",
  outline: "none",
  userSelect: "text",
  whiteSpace: "pre-wrap",
  ":focus": {
    outline: "none",
  },
};
const placeholderStyle: Interpolation = {
  // this prevents iOS Safari from being a little punk and refusing to focus
  // the first time you click the editable div
  pointerEvents: "none",
};

type ContentEditableDivProps = Omit<TextProps, "as"> &
  Omit<HallowElementProps<"div">, "onChange" | "children"> & {
    disabled?: boolean;
    required?: boolean;
    onChange?: (e: ChangeEvent<HTMLDivElement>) => void;
    children?: string;
    placeholder?: ReactNode;
    placeholderCSS?: Interpolation;
    maxLength?: number;
  };

type ContentEditableDivFieldProps<T extends FieldValues = FieldValues> =
  ContentEditableDivProps & {
    control: Control<T>;
    name: Path<T>;
  };

/** Plain text from a contenteditable `div` (handles WebKit/Firefox markup). */
export const getPlainTextFromContentEditable = (
  parentElement: HTMLDivElement,
): string => {
  if (!parentElement) return "";
  if (parentElement.innerHTML === parentElement.textContent)
    return parentElement.textContent ?? "";
  // normally I wouldn't do HTML analysis with Regex, but we sanitize this at the end if it has anything
  // more complicated than the super-simple <br> (Firefox) and <div> (Webkit) elements added via a
  // contenteditable DIV
  let html = parentElement.innerHTML;
  html = html.replaceAll(/<[dD][iI][vV]><[bB][rR] ?\/?>/g, "\n"); // combos first, thanks Chrome
  html = html.replaceAll(/<[bB][rR] ?\/?>/g, "\n"); // brs alone, Firefox
  html = html.replaceAll(/<[dD][iI][vV]>/g, "\n"); // divs alone, webkit
  html = html.replaceAll(/<\/[dD][iI][vV]>/g, ""); // stragglers
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  return doc.body.textContent ?? "";
};

export const ContentEditableDiv = forwardRef<
  HTMLDivElement,
  ContentEditableDivProps
>(
  (
    {
      disabled,
      children,
      onChange,
      onInput,
      onBlur,
      onFocus,
      size,
      type,
      placeholder,
      placeholderCSS = {},
      maxLength = null,
      ...props
    },
    ref,
  ) => {
    const fireChange = useRef<boolean>(false);
    const [value, setValue] = useState<string>(children);
    const [focused, setFocused] = useState<boolean>(false);

    useEffect(() => {
      if (!focused) {
        setValue(children ?? "");
      }
    }, [children, focused]);

    const handleFocus = (e) => {
      if (!focused) {
        setFocused(true);
      }
      onFocus?.(e);
    };

    const handleInput = (e) => {
      fireChange.current = true;
      onChange(e);
      onInput?.(e);
    };

    const handleBlur: FocusEventHandler<HTMLDivElement> = (e) => {
      if (fireChange.current) {
        fireChange.current = false;
        onChange(e);
        setValue(
          // `slice(0, null)` truncates to an empty string, so an absent
          // maxLength has to become `undefined` rather than `null`
          getPlainTextFromContentEditable(e.currentTarget).slice(
            0,
            maxLength ?? undefined,
          ),
        );
      }
      onBlur?.(e);
      setFocused(false);
    };

    const handleBeforeInput: InputEventHandler<HTMLDivElement> = (e) => {
      if (
        maxLength !== null &&
        ((e.currentTarget as HTMLDivElement)?.textContent?.length ?? 0) +
          ((e.nativeEvent as InputEvent)?.data?.length ?? 0) >=
          maxLength
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handlePaste: ClipboardEventHandler<HTMLDivElement> = (e) => {
      let current = getPlainTextFromContentEditable(e.currentTarget);
      if (maxLength !== null && current.length >= maxLength) {
        e.preventDefault();
        return;
      }
      if (maxLength !== null) {
        let interrupted = false;
        for (const typ of e.clipboardData.types) {
          if (typ === "text/plain") {
            const paste = e.clipboardData.getData(typ);
            if (current.length + paste.length > maxLength) {
              current += paste.slice(0, maxLength - current.length);
              interrupted = true;
            }
          }
        }
        if (interrupted) {
          setValue(current);
          // the paste never reaches the DOM, so flag it for handleBlur to
          // push into the parent — otherwise the truncated text is displayed
          // but never saved
          fireChange.current = true;
          e.preventDefault();
        }
      }
    };

    return (
      <Text
        asChild
        {...({ size, type } as TextProps)}
        css={divStyle}
        {...props}
      >
        <div
          contentEditable={!disabled}
          suppressContentEditableWarning={true}
          tabIndex={disabled ? undefined : 0}
          {...props}
          ref={ref}
          onInput={handleInput}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onBeforeInput={handleBeforeInput}
          onPaste={handlePaste}
        >
          {!focused && placeholder && !value?.length ? (
            <span css={[placeholderStyle, placeholderCSS]}>{placeholder}</span>
          ) : (
            value
          )}
        </div>
      </Text>
    );
  },
) as ForwardRefExoticComponent<
  Omit<ContentEditableDivProps, "ref"> & RefAttributes<HTMLDivElement>
> & {
  Field: <T>(props: ContentEditableDivFieldProps<T>) => JSX.Element;
};

ContentEditableDiv.displayName = "ContentEditableDiv";

ContentEditableDiv.Field = ({ control, name, required, ...props }) => {
  const {
    field: { ref, value, ...field },
  } = useController({
    name,
    control,
    rules: { required },
  });

  return (
    <ContentEditableDiv
      {...field}
      {...props}
      ref={ref}
      onChange={(event) =>
        field.onChange?.(
          getPlainTextFromContentEditable(event?.target as HTMLDivElement),
        )
      }
    >
      {value.toString()}
    </ContentEditableDiv>
  );
};
